import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { InputGroup } from '@blueprintjs/core';
import { Button } from '@launchpad-ui/components';

import { CircularIconButton } from '../../../shared';
import { Box, Flex } from '../../../shared/components';
import {
  AUDIT_LOG_EXPORT_MAX_ENTRIES_CAP,
  AUDIT_LOG_EXPORT_MAX_ENTRIES_DEFAULT,
  AUDIT_LOG_EXPORT_PAGE_LIMIT,
  clampAuditLogMaxEntries,
  downloadAuditLogExportJson,
  fetchAllAuditLogPagesForExport,
} from '../../auditLogExport';
import type { Account } from '../../model';

type AuditLogExportProps = Readonly<{
  account: Account;
  query: string;
  spec: string;
  member?: string;
  fuzzy?: boolean;
  date: ReadonlyArray<Date | null | number>;
}>;

const AuditLogExportTooltip = () => (
  <div>
    <p>
      Uses the same filters as Search (resource specifier, query, member, date range). Stops after the max number of
      entries you set (after date filtering).
    </p>
    <p>
      While a run is in progress, use <strong>Stop export</strong> to cancel. If any entries were already fetched, a
      partial JSON file is downloaded (<code className="bp4-code">meta.userAborted: true</code>).
    </p>
    <p>
      Paginates <code className="bp4-code">GET /private/accounts/:id/auditlog</code> at {AUDIT_LOG_EXPORT_PAGE_LIMIT}{' '}
      per request and respects rate limits.
    </p>
    <p>The on-screen list uses rollup; this export does not, so row counts may differ.</p>
  </div>
);

export function AuditLogExport({ account, query, spec, member, fuzzy, date }: AuditLogExportProps) {
  const [maxEntriesInput, setMaxEntriesInput] = useState(String(AUDIT_LOG_EXPORT_MAX_ENTRIES_DEFAULT));
  const [status, setStatus] = useState('');
  const [exporting, setExporting] = useState(false);
  const exportAbortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      exportAbortRef.current?.abort();
    };
  }, []);

  const toEpochMs = (d: Date | null | number | undefined): number | undefined => {
    if (d === null || d === undefined) {
      return undefined;
    }
    return typeof d === 'number' ? d : d.getTime();
  };
  const afterMs = toEpochMs(date[0] ?? undefined);
  const beforeMs = toEpochMs(date[1] ?? undefined);

  const handleMaxEntriesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMaxEntriesInput(e.currentTarget.value);
  };

  const cancelExport = useCallback(() => {
    exportAbortRef.current?.abort();
  }, []);

  const runExport = useCallback(async () => {
    const maxEntries = clampAuditLogMaxEntries(Number.parseInt(maxEntriesInput, 10));
    setMaxEntriesInput(String(maxEntries));

    exportAbortRef.current?.abort();
    const ac = new AbortController();
    exportAbortRef.current = ac;
    setExporting(true);
    setStatus('Starting export…');

    try {
      const payload = await fetchAllAuditLogPagesForExport({
        account,
        afterMs,
        beforeMs,
        limit: AUDIT_LOG_EXPORT_PAGE_LIMIT,
        maxEntries,
        spec: spec.trim() || undefined,
        query: query.trim() || undefined,
        member: member?.trim() || undefined,
        fuzzy,
        signal: ac.signal,
        onProgress: (message) => {
          if (isMountedRef.current) {
            setStatus(message);
          }
        },
      });

      if (!isMountedRef.current) {
        return;
      }

      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `exported_auditlog_${account._id}_${stamp}.json`;

      if (payload.meta.userAborted && payload.meta.totalItems === 0) {
        setStatus('Export stopped — no records had been saved yet.');
      } else {
        if (payload.meta.totalItems > 0) {
          downloadAuditLogExportJson(payload, filename);
        }

        if (payload.meta.userAborted) {
          setStatus(`Export stopped — saved ${filename} (${payload.meta.totalItems} records, partial export).`);
        } else if (payload.meta.totalItems === 0) {
          setStatus('Export finished — no matching records (no file downloaded).');
        } else {
          const capNote = payload.meta.truncatedAtMaxEntries
            ? ` (capped at ${payload.meta.maxEntriesRequested}; more entries exist).`
            : '';
          setStatus(
            payload.meta.exportComplete && !payload.meta.truncatedAtMaxEntries
              ? `Downloaded ${filename} (${payload.meta.totalItems} records).`
              : `Downloaded ${filename} (${payload.meta.totalItems} records)${capNote}`,
          );
        }
      }
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Export failed.';
      setStatus(message);
    } finally {
      if (exportAbortRef.current === ac) {
        exportAbortRef.current = null;
        if (isMountedRef.current) {
          setExporting(false);
        }
      }
    }
  }, [account, afterMs, beforeMs, fuzzy, maxEntriesInput, member, query, spec]);

  return (
    <Box className="AuditLogEntry-export">
      <Flex alignItems="center" mb={2} style={{ gap: 6 }}>
        <span className="AuditLogEntry-export-title">Export audit log (JSON)</span>
        <CircularIconButton
          iconName="info"
          intent="neutral"
          size="small"
          tooltip={<AuditLogExportTooltip />}
          tooltipPlacement="top"
          ariaLabel="Audit log export details"
        />
      </Flex>
      <Flex alignItems="flex-end" flexWrap="wrap" className="AuditLogEntry-export-controls">
        <Box className="AuditLogEntry-exportField">
          <label className="AuditLogEntry-label" htmlFor="auditLogExportMaxEntries">
            Max entries <span className="bp4-text-muted">(1–{AUDIT_LOG_EXPORT_MAX_ENTRIES_CAP})</span>
          </label>
          <InputGroup
            id="auditLogExportMaxEntries"
            type="number"
            min={1}
            max={AUDIT_LOG_EXPORT_MAX_ENTRIES_CAP}
            className="AuditLogEntry-search"
            value={maxEntriesInput}
            onChange={handleMaxEntriesChange}
            disabled={exporting}
            style={{ maxWidth: 140 }}
          />
        </Box>
        <Box className="AuditLogEntry-exportActions">
          <Button
            variant="primary"
            className="AuditLogEntry-exportDownloadAction"
            onPress={runExport}
            isDisabled={exporting}
          >
            Download export
          </Button>
          {exporting ? (
            <Button
              variant="minimal"
              onPress={cancelExport}
              style={{
                backgroundColor: '#e94b3c',
                border: '1px solid #e94b3c',
                color: '#fff',
              }}
            >
              Stop export
            </Button>
          ) : null}
        </Box>
      </Flex>
      {status ? (
        <Box className="AuditLogEntry-export-status" role="status">
          {status}
        </Box>
      ) : null}
    </Box>
  );
}
